<template>
  <div>
    <!-- Show convenience fee message -->
    <ConvenienceFeeMessageComponent />
    <div class="row custom-amount-container">
      <div class="cell">
        <label
          v-if="showCreditCardLimitDisclaimer()"
          for="pay-amount"
        >Amount (${{ invoice.remaing_digital_wallet_amount }} limit)</label>
        <label
          v-else
          for="pay-amount"
        >Amount</label>
        <input
          id="pay-amount"
          v-model="customAmount"
          class="input amount-input"
          name="pay-amount"
          type="number"
          @change="customAmountChanged"
        >
        <label
          v-if="showCreditCardLimitDisclaimer()"
          class="amount-disclaimer"
        >Credit Card and digital wallets are limited to $15,000. Balance will be due by Zelle, ACH, or Wire Transfer.</label>
      </div>
    </div>
    <!-- Show loading spinner if loading -->
    <LoadingComponent v-if="isLoading" />
    <!-- Show Venmo button if payment not ready -->
    <div
      v-show="!error && !isLoading && !venmoPaymentIsReady"
      class="venmno-button-container"
    >
      <span class="centered">Complete purchase with</span>
      <button
        id="venmo-pay-button"
        :disabled="isLoading"
      >
        <img
          :src="require(`@/assets/checkout/blue_venmo_button_280x48.svg`)"
          alt="Pay with Venmo"
        >
      </button>
    </div>
    <!-- Show Venmo username if payment is ready to complete -->
    <div
      v-if="venmoPaymentIsReady"
      class="venmo-success-container"
    >
      <div class="icon">
        <img
          :src="require(`@/assets/checkout/blue_venmo_acceptance_mark.svg`)"
          alt="Venmo acceptance mark"
        >
      </div>
      <div class="username-container">
        <div class="username">
          {{ paymentFormStore.venmoDetails.username }}
        </div>
        <div>Venmo</div>
      </div>
      <div class="checkmark">
        <span class="green-check">&#10004;</span>
      </div>
    </div>
    <!-- Show error message if error -->
    <BraintreeErrorMessageComponent
      v-if="error"
      :error="error"
    />
    <!-- End the Venmo component -->
  </div>
</template>
<script>
import BraintreeError from '../../../../errors/BraintreeError'
import BraintreeErrorMessageComponent from '../../Shared/BraintreeErrorMessageComponent.vue'
import LoadingComponent from '../../Shared/LoadingComponent.vue'
import ConvenienceFeeMessageComponent from '../../Shared/ConvenienceFeeMessageComponent.vue'

export default {
  name: 'VenmoComponent',
  components: {
    BraintreeErrorMessageComponent,
    ConvenienceFeeMessageComponent,
    LoadingComponent
  },
  props: ['braintreeInstance', 'invoice', 'disablePaymentMethod', 'deviceData'],
  data: function () {
    return {
      isLoading: true,
      error: null,
      venmoButton: null,
      venmoInstance: null,
      customAmount: 0,
    }
  },
  computed: {
    paymentFormStore () {
      return this.$store.getters.paymentForm
    },
    venmoOptions () {
      // Set to true to debug on Desktop
      const allowDesktop = false
      const allowDesktopWebLogin = false
      // Set to false to force mobile customer to use the Venmo app
      const mobileWebFallBack = false
      // Set to false because hasTokenizationResult() not working in our case
      const allowNewBrowserTab = false
      // Set to "single_use" to only allow one-time payments (vaulting not supported)
      const paymentMethodUsage = 'multi_use'
      return {
        allowDesktop,
        allowDesktopWebLogin,
        mobileWebFallBack,
        allowNewBrowserTab,
        paymentMethodUsage
      }
    },
    venmoPaymentIsReady () {
      if (this.paymentFormStore.paymentMethod === 'venmo') {
        if (
          this.paymentFormStore.nonce &&
          this.paymentFormStore.nonce.length > 0
        ) {
          if (
            this.paymentFormStore.venmoDetails &&
            this.paymentFormStore.venmoDetails.username
          ) {
            return true
          }
        }
      }
      return false
    }
  },
  async mounted () {
    this.venmoButton = window.document.getElementById('venmo-pay-button')
    await this.init()
  },
  methods: {
    /**
     * Init the venmo button
     */
    init: async function () {
      if(this.invoice !== undefined){
        this.customAmount = this.invoice.remaing_digital_wallet_amount ? this.invoice.remaing_digital_wallet_amount.toFixed(2) : 0
      }
      // Get the venmo instance
      this.venmoInstance = await window.braintree.venmo
        .create({
          client: this.braintreeInstance,
          ...this.venmoOptions
        })
        .catch(this.handleVenmoError)

      if (!this.venmoInstance) {
        console.error('Error creating Venmo instance')
        this.isLoading = false
        this.disable()
        return
      }

      // Verify browser support before proceeding.
      if (!this.venmoInstance.isBrowserSupported()) {
        console.error('Browser does not support Venmo')
        this.error = 'Browser does not support Venmo'
        this.isLoading = false
        this.disable()
        return
      }

      // Show the Venmo button
      this.displayVenmoButton(this.venmoInstance)

      // Only go into here if allowNewBrowserTab is true
      if (this.venmoOptions.allowNewBrowserTab) {
        // Check if tokenization results already exist. This occurs when your
        // checkout page is relaunched in a new tab. This step can be omitted
        // if allowNewBrowserTab is false.
        if (this.venmoInstance.hasTokenizationResult()) {
          const payload = await this.venmoInstance.tokenize().catch((err) => {
            this.handleVenmoError(err)
          })
          if (!payload) {
            console.error('Venmo tokenization failed')
            this.isLoading = false
            return
          }
          await this.handleVenmoSuccess(payload)
          this.isLoading = false
          return
        }
        this.error = 'Venmo failed to initialize'
        this.isLoading = false
        return
      }

      // Successfully initialized.
      this.isLoading = false
    },
    customAmountChanged: function (event) {
      const current = event.target.value
      const amount = parseFloat(current)
      if(amount > this.invoice.remaing_digital_wallet_amount){
        this.customAmount = this.invoice.remaing_digital_wallet_amount
      }
      else{
        this.customAmount = amount
      }
    },
    showCreditCardLimitDisclaimer: function () {
      return this.invoice.invoice_total > 15000
    },
    /**
     * Display Venmo button
     */
    displayVenmoButton: async function (venmoInstance) {
      // Assumes that venmoButton is initially display: none.
      this.venmoButton.style.display = 'block'
      // Listen for the click.
      this.venmoButton.addEventListener('click', async () => {
        this.venmoButton.disabled = true
        const payload = await venmoInstance.tokenize().catch((err) => {
          this.handleVenmoError(err)
        })
        if (!payload) {
          this.error = 'Venmo failed to initialize'
          return
        }
        this.venmoButton.removeAttribute('disabled')
        await this.handleVenmoSuccess(payload)
      })
    },
    /**
     * Handle Venmo errors
     */
    handleVenmoError: function (err) {
      if (err) {
        const exception = new BraintreeError(err)
        const { code } = err
        switch (code) {
          // If customer closes Venmo window, re-enable the Venmo button
          case 'FRAME_SERVICE_FRAME_CLOSED':
          case 'VENMO_CANCELED':
          case 'VENMO_APP_CANCELED':
            this.venmoButton.removeAttribute('disabled')
            break
          // Otherwise, display an error message
          default:
            this.error = exception.getMessage()
            break
        }
      }
    },
    /**
     * Handle Venmo success
     */
    handleVenmoSuccess: async function (payload) {
      const { nonce, details } = payload
      const paymentForm = {
        nonce: nonce,
        type: 'venmo',
        submitCC: false,
        venmoDetails: details,
        deviceData: this.deviceData,
        isPartialPayment: this.invoice.invoice_balance == this.customAmount ? false : true,
        amount: this.customAmount,
        savedPaymentMethod: false
      }
      await this.$store.dispatch('updatePaymentForm', paymentForm)
    },
    /**
     * To force a payment method to be hidden from list.
     */
    disable: function () {
      this.disablePaymentMethod(this.$options.name)
    }
  }
}
</script>
<style scoped>
.venmno-button-container {
  padding-top: 1em;
}
#venmo-pay-button {
  display: none;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  padding: 0; /* Remove any default padding to fit the image */
  background-color: transparent; /* Optional: remove background color */
}
.centered {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
.venmo-success-container {
  height: 5em;
  border-radius: 0.3em;
  width: 100%;
  display: flex;
  align-items: center;
}
.venmo-success-container .username-container {
  flex: 1;
}
.venmo-success-container .username-container .username {
  font-weight: 800;
}
.icon {
  padding: 0.5em;
  margin-right: 0.25em;
}
.checkmark {
  margin-right: 0.5em;
}
.green-check {
  color: green;
  font-size: xx-large;
}

.custom-amount-container {
  max-width: 400px;
  width: 100%;
}

.amount-disclaimer {
  text-align: center;
  padding-top: 0.5rem;
  padding-bottom: 1rem;
  font-size: 0.8em;
}
</style>
